import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

/**
 * Mécanisme générique de « réinitialisation de page » déclenché par le fil d'Ariane.
 *
 * BUT — « comme partout, cliquer sur le fil d'Ariane en haut revient à l'état INITIAL
 * de la page ». Pour les écrans dont l'état vit dans l'URL (galerie : tri/filtres/
 * recherche dérivés de `location.search`), naviguer vers le chemin de base propre
 * suffit : l'état se reconstruit aux valeurs par défaut. MAIS certaines pages tiennent
 * leur état en état React LOCAL (ex. la revue de dédoublonnage : page courante, choix
 * de keepers, seuil…). Un clic sur le maillon courant du fil pointe vers le MÊME
 * chemin → React Router ne remonte pas le composant → l'état local ne bouge pas.
 *
 * Ce contexte comble ce trou : une page appelle `usePageReset(resetFn)` pour ENREGISTRER
 * un rappel de réinitialisation ; le maillon COURANT du fil d'Ariane appelle
 * `usePageResetTrigger()` et déclenche le rappel enregistré à l'activation (en plus de
 * naviguer vers le chemin de base propre). Ainsi, même un clic « même chemin » réinitialise
 * l'état local.
 *
 * Contrat :
 * - UN SEUL rappel actif à la fois (la page montée gagne). C'est volontaire : un seul
 *   écran « page courante » est rendu sous l'`<Outlet>` à un instant donné.
 * - Le rappel est stocké dans une ref (pas d'état) → l'enregistrement ne provoque aucun
 *   re-render, et le déclencheur lit toujours la dernière fonction fournie.
 * - Enregistrer `null`/ne pas appeler `usePageReset` = pas de reset local (les pages
 *   pilotées par l'URL n'ont rien à enregistrer : la navigation vers la base propre suffit).
 * - `usePageReset` nettoie automatiquement au démontage (retire SON rappel s'il est encore
 *   le rappel courant → ne piétine pas une page qui aurait déjà pris le relais).
 *
 * ⚠ Consommation à venir (prochain lot) : `DedupReviewPage` appellera
 *   `usePageReset(reset)` où `reset` vide les sélections, remet la page à 0 et scrolle
 *   en haut. Aucune page n'est modifiée ici en dehors du câblage du contexte.
 */

/** Rappel de réinitialisation d'une page (sans argument, sans retour). */
type ResetFn = () => void;

interface PageResetContextValue {
  /** Enregistre le rappel de reset de la page courante ; retourne un désenregistreur. */
  register: (fn: ResetFn) => () => void;
  /** Déclenche le rappel enregistré (no-op s'il n'y en a pas). */
  trigger: () => void;
}

const PageResetContext = createContext<PageResetContextValue | null>(null);

/**
 * Fournisseur du mécanisme de reset de page. À monter là où le fil d'Ariane et
 * l'`<Outlet>` des pages partagent le même sous-arbre (cf. `AppChrome`).
 */
export function PageResetProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const resetRef = useRef<ResetFn | null>(null);

  const register = useCallback((fn: ResetFn): (() => void) => {
    resetRef.current = fn;
    return () => {
      // Ne retire QUE si c'est toujours notre rappel (une autre page a pu prendre la main).
      if (resetRef.current === fn) resetRef.current = null;
    };
  }, []);

  const trigger = useCallback(() => {
    resetRef.current?.();
  }, []);

  const value = useMemo<PageResetContextValue>(() => ({ register, trigger }), [register, trigger]);

  return <PageResetContext.Provider value={value}>{children}</PageResetContext.Provider>;
}

/**
 * Enregistre le rappel de réinitialisation de la page COURANTE, appelé quand
 * l'utilisateur active le maillon courant du fil d'Ariane. Le rappel est rafraîchi à
 * chaque rendu (une ref interne pointe toujours vers la dernière version), et il est
 * automatiquement désenregistré au démontage de la page.
 *
 * Hors d'un `PageResetProvider`, c'est un no-op inoffensif (utile en tests isolés).
 */
export function usePageReset(reset: ResetFn): void {
  const ctx = useContext(PageResetContext);
  // Garde la dernière closure sans re-enregistrer à chaque rendu (identité stable).
  const latest = useRef(reset);
  latest.current = reset;

  useEffect(() => {
    if (!ctx) return;
    return ctx.register(() => latest.current());
  }, [ctx]);
}

/**
 * Récupère le déclencheur de reset (pour le maillon courant du fil d'Ariane). Hors
 * d'un `PageResetProvider`, retourne un no-op.
 */
export function usePageResetTrigger(): () => void {
  const ctx = useContext(PageResetContext);
  return ctx?.trigger ?? noop;
}

function noop(): void {
  /* pas de fournisseur → rien à déclencher */
}
